/**
 * Specialty Installment options — added Feb 19, 2026.
 *
 * Operationally separate from the standard 1st Half / 2nd Half / Q1–Q4
 * installments that the Tax Collection toggle auto-generates. Specialty
 * rows are selected MANUALLY by processors via the Installment dropdown
 * in Tax Records.
 *
 * Design constraints (locked with user):
 *   - Do NOT modify the existing notes engine. Specialty wording is
 *     emitted as a SEPARATE additive "Specialty Tax Notes" block that
 *     callers append after the normal engine output runs.
 *   - SB / SB-1 / SB-2 are available ONLY for California (CA) properties.
 *   - SA / SA-1 / Water / Sewer / Storm Water / Water & Sewer are
 *     available in every state.
 *   - Specialty rows must SURVIVE Tax Collection regeneration so a
 *     processor doesn't lose an SB-1 row by flipping Semi-Annual →
 *     Quarterly.
 *   - Tax Year remains required for every specialty bill type.
 *   - The selected installment label flows verbatim into the Tax
 *     Summary UI, the PDF cert, and Excel/CSV exports — no schema or
 *     PDF layout changes.
 */

// ── Specialty option groups ──────────────────────────────────────────
export const SUPPLEMENTAL_BILL_OPTIONS = ['SB', 'SB-1', 'SB-2'];
export const SPECIAL_ASSESSMENT_OPTIONS = ['SA', 'SA-1'];
export const UTILITY_BILL_OPTIONS = ['Water', 'Sewer', 'Storm Water', 'Water & Sewer', 'Solid Waste'];

// ── Standard installment labels by Tax Collected frequency ───────────
// Single source of truth used by:
//   1. JurisdictionManager.jsx Tax Collected frequency-click handler
//      (regenerates standard rows on freq switch — keeps specialty
//      rows verbatim per PRESERVE-SPECIALTY contract).
//   2. The per-row Installment dropdown — so that once a row is
//      switched to a Specialty option (e.g. Solid Waste) the user
//      can still flip it back to Q1 / Q2 / Q3 / Q4 / 1st Half /
//      2nd Half / Annual without having to delete the row.
//
// Keep the keys in sync with `collection_frequency` values stored
// on the jurisdiction document.
// TAXCERT-INSTALLMENT-PLAN-001 (Sep 2026) — `installment_plan` seeds ONE row
// ("1st Installment"); the processor adds rows which are auto-named by
// `installmentPlanLabel(n)`. The dropdown exposes 1st–6th.
export const INSTALLMENT_PLAN_MAX = 6;
export const INSTALLMENT_PLAN_LABELS = Object.freeze(
  ['1st', '2nd', '3rd', '4th', '5th', '6th'].map((o) => `${o} Installment`)
);
export const installmentPlanLabel = (n) =>
  INSTALLMENT_PLAN_LABELS[Math.min(Math.max(n, 1), INSTALLMENT_PLAN_MAX) - 1];
/** "3rd Installment" / "Installment 3" / "third installment" → 3 (0 when not an installment-plan label). */
export const installmentPlanOrdinal = (name) => {
  const n = String(name || '').trim().toLowerCase();
  if (!n.includes('installment')) return 0;
  const m = n.match(/(\d)(?:st|nd|rd|th)?/);
  if (m) return parseInt(m[1], 10);
  const words = ['first', 'second', 'third', 'fourth', 'fifth', 'sixth'];
  const w = words.findIndex((x) => n.includes(x));
  return w >= 0 ? w + 1 : 0;
};

export const STANDARD_INSTALLMENT_LABELS = Object.freeze({
  annually:      ['Annual'],
  semi_annually: ['1st Half', '2nd Half'],
  quarterly:     ['Q1', 'Q2', 'Q3', 'Q4'],
  installment_plan: [INSTALLMENT_PLAN_LABELS[0]],
});

/**
 * Return the ordered standard installment labels for the given Tax
 * Collected frequency. Falls back to `['Annual']` for unknown / empty
 * inputs so the dropdown is never blank.
 */
export const getStandardInstallmentOptions = (frequency) => {
  const key = (frequency || '').toLowerCase();
  if (key === 'installment_plan') return [...INSTALLMENT_PLAN_LABELS];
  return STANDARD_INSTALLMENT_LABELS[key] || ['Annual'];
};

// States in which the Supplemental Bill group is exposed in the UI.
// Centralised so widening to another state is a one-line change.
export const SUPPLEMENTAL_BILL_STATES = ['CA'];

// Flat union used by validators, regeneration guards, notes generator, exports.
export const SPECIALTY_INSTALLMENT_OPTIONS = [
  ...SUPPLEMENTAL_BILL_OPTIONS,
  ...SPECIAL_ASSESSMENT_OPTIONS,
  ...UTILITY_BILL_OPTIONS,
];

const SPECIALTY_SET = new Set(SPECIALTY_INSTALLMENT_OPTIONS.map((v) => v.toLowerCase()));

/**
 * Predicate — true when the given installment_name maps to a specialty
 * (SB / SA / utility) row, false for standard 1st Half / 2nd Half /
 * Q1-Q4 / Annual / Michigan Season labels.
 *
 * Case-insensitive on the stored label.
 */
export const isSpecialtyInstallment = (name) => {
  if (!name || typeof name !== 'string') return false;
  return SPECIALTY_SET.has(name.trim().toLowerCase());
};

/**
 * Resolve which specialty options the dropdown should expose for the
 * current property state. Non-CA states get the SB family removed; SA +
 * utility groups stay universal.
 */
export const getAvailableSpecialtyOptions = (stateCode) => {
  const code = (stateCode || '').toUpperCase();
  const groups = [];
  if (SUPPLEMENTAL_BILL_STATES.includes(code)) {
    groups.push({ label: 'Supplemental Bill (CA only)', options: SUPPLEMENTAL_BILL_OPTIONS });
  }
  groups.push({ label: 'Special Assessment', options: SPECIAL_ASSESSMENT_OPTIONS });
  groups.push({ label: 'Utility Bills', options: UTILITY_BILL_OPTIONS });
  return groups;
};

/**
 * Group an installment_name into a human-readable category for the
 * additive Specialty Tax Notes block.
 */
export const getSpecialtyCategory = (name) => {
  if (!name) return null;
  const n = name.trim();
  if (SUPPLEMENTAL_BILL_OPTIONS.includes(n)) return 'supplemental_bill';
  if (SPECIAL_ASSESSMENT_OPTIONS.includes(n)) return 'special_assessment';
  if (UTILITY_BILL_OPTIONS.includes(n)) return 'utility';
  return null;
};

/**
 * Currency formatter used inside specialty notes — mirrors the
 * negative-zero guard already applied across the app so we never
 * print "$-0.00" on a specialty paid-at-zero row.
 */
const fmtUsd = (v) => {
  const num = typeof v === 'string' ? parseFloat(v.replace(/[^0-9.-]/g, '')) : Number(v);
  if (!Number.isFinite(num)) return '$0.00';
  const safe = Math.abs(num) < 0.005 ? 0 : num;
  return `$${safe.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/**
 * Format a stored date string (ISO `YYYY-MM-DD` or `MM/DD/YYYY`) into
 * the `MM/DD/YYYY` shape used inside specialty notes wording.
 * Returns '' for empty / unparseable input.
 */
const fmtMDY = (v) => {
  if (!v) return '';
  const s = String(v).trim();
  if (!s) return '';
  // Already MM/DD/YYYY → pass through
  const usMatch = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (usMatch) {
    const [, m, d, y] = usMatch;
    return `${m.padStart(2, '0')}/${d.padStart(2, '0')}/${y}`;
  }
  // ISO YYYY-MM-DD
  const isoMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const [, y, m, d] = isoMatch;
    return `${m}/${d}/${y}`;
  }
  return s;
};

const computeBalance = (inst) => {
  const base = parseFloat(inst.base_tax) || 0;
  const adj = parseFloat(inst.discount_penalty) || 0;
  const paid = parseFloat(inst.paid_amount) || 0;
  return base + adj - paid;
};

const isPaid = (inst) => {
  const status = (inst.pay_status || '').toLowerCase().trim();
  // Explicit fully-paid statuses always satisfy isPaid regardless of
  // computed balance. "Partially Paid" / "Partially Delinquent" are
  // intentionally NOT in this list — they imply a non-zero balance.
  if (status === 'paid' || status === 'paid in full') return true;
  return computeBalance(inst) <= 0.005;
};

/**
 * Just the verb portion of the operational specialty sentence.
 *
 * Returns the row's actual `pay_status` value verbatim (with a single
 * normalisation: a fully-paid row always reads as the lowercase token
 * "paid" to match the operational convention requested by the user).
 *
 * Supports every status the system already exposes — Paid, Due,
 * Delinquent, Partially Delinquent, Partially Paid, Paid in Full,
 * and any future label added to the dropdown — without code changes.
 *
 *   - fully paid row              → "paid" (lowercase — always)
 *   - pay_status === "Paid"       → "paid"
 *   - pay_status === "Due"        → "Due"
 *   - pay_status === "Delinquent" → "Delinquent"
 *   - "Partially Delinquent"      → "Partially Delinquent"
 *   - "Partially Paid"            → "Partially Paid"
 *   - missing/empty               → "Due" (safe operational default)
 */
const statusWord = (inst) => {
  if (isPaid(inst)) return 'paid';
  const raw = (inst.pay_status || '').toString().trim();
  if (!raw) return 'Due';
  if (raw.toLowerCase() === 'paid') return 'paid';
  return raw;
};

/**
 * Ordinal label that prefixes the bill name inside specialty wording.
 *   SB-1 → "1st", SB-2 → "2nd", SA-1 → "1st", plain SB/SA → "" (omitted).
 *   Utility types never get a numeric ordinal.
 */
const installmentOrdinal = (label) => {
  if (label === 'SB-1' || label === 'SA-1') return '1st';
  if (label === 'SB-2') return '2nd';
  return '';
};

/**
 * Bill noun used in the sentence. Strips the "-1"/"-2" suffix so the
 * ordinal carries the installment relationship instead.
 *   SB / SB-1 / SB-2  → "SB"
 *   SA / SA-1         → "SA"
 *   Water / Sewer …   → as-is.
 */
const installmentNoun = (label) => {
  if (label === 'SB' || label === 'SB-1' || label === 'SB-2') return 'SB';
  if (label === 'SA' || label === 'SA-1') return 'SA';
  return label;
};

/**
 * Resolve the canonical (case-correct) form of a stored installment
 * label so the ordinal/noun helpers don't get tripped up by legacy
 * lowercase data such as `"sb-1"` or `"water"`.
 */
const canonicalInstallmentName = (raw) => {
  const trimmed = (raw || '').toString().trim();
  if (!trimmed) return '';
  for (const opt of SPECIALTY_INSTALLMENT_OPTIONS) {
    if (trimmed.toLowerCase() === opt.toLowerCase()) return opt;
  }
  return trimmed;
};

/**
 * Sentence fragment for a single specialty row — e.g.
 *   "1st SB bill is paid"
 *   "Water bill is Due"
 * Year is NOT included here; the per-group sentence renders the year
 * once up-front.
 */
const fragmentForRow = (inst) => {
  const canonical = canonicalInstallmentName(inst.installment_name);
  const ord = installmentOrdinal(canonical);
  const noun = installmentNoun(canonical);
  const verb = statusWord(inst);
  return ord ? `${ord} ${noun} Bill is ${verb}` : `${noun} Bill is ${verb}`;
};

/**
 * Join an ordered list with English series commas:
 *   ["1st"]                  → "1st"
 *   ["1st", "2nd"]           → "1st and 2nd"
 *   ["1st", "2nd", "3rd"]    → "1st, 2nd and 3rd"
 */
const joinSeries = (parts) => {
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
};

/**
 * Operational sentence that names which bills hold which status.
 * Optimised to collapse same-status groups into a single shared verb
 * ("1st and 2nd SB bills are paid") while preserving explicit per-row
 * wording when statuses differ.
 *
 * Examples:
 *   single row:                  "1st SB bill is paid"
 *   two rows, same status:       "1st and 2nd SB bills are paid"
 *   two utility rows, same:      "Water and Sewer bills are paid"
 *   two rows, mixed status:      "1st SB bill is paid and 2nd SB bill is Due"
 *   plain SB (no ordinal):       "SB bill is paid"
 */
const buildBillSentence = (rows) => {
  if (rows.length === 1) return fragmentForRow(rows[0]);

  const verbs = new Set(rows.map(statusWord));
  if (verbs.size === 1) {
    const sharedVerb = [...verbs][0];
    const canonicals = rows.map((r) => canonicalInstallmentName(r.installment_name));
    const ordinals = canonicals.map(installmentOrdinal);
    const nouns = canonicals.map(installmentNoun);

    const everyOrdinal = ordinals.every(Boolean);
    const uniqueNouns = [...new Set(nouns)];

    // SB / SA combined form — shared noun, distinct ordinals.
    if (everyOrdinal && uniqueNouns.length === 1) {
      return `${joinSeries(ordinals)} ${uniqueNouns[0]} Bills are ${sharedVerb}`;
    }

    // Utility / no-ordinal combined form — distinct nouns share a verb.
    const noOrdinal = ordinals.every((o) => !o);
    if (noOrdinal) {
      return `${joinSeries(nouns)} Bills are ${sharedVerb}`;
    }
  }

  // Mixed status (or noun/ordinal shapes that don't merge cleanly) —
  // keep explicit per-row wording so each bill's state is visible.
  return joinSeries(rows.map(fragmentForRow));
};

const groupSentence = (heading, rows) => {
  if (rows.length === 0) return '';
  // Pick the shared tax year — fall back to the first non-blank one.
  const year = (rows.find((r) => r.tax_year)?.tax_year || '').toString().trim();
  const yearPrefix = year ? `${year} ` : '';

  const dueDates = rows.map((r) => fmtMDY(r.due_date)).filter(Boolean);
  const dlqDates = rows
    .map((r) => fmtMDY(r.dlq_date || r.delinquent_date || r.delinquency_date))
    .filter(Boolean);

  // Bill-status sentence (handles same-status grouping vs mixed-status).
  const billSentence = `${yearPrefix}${buildBillSentence(rows)}.`;

  // Combined Due / DLQ sentence — Feb 19, 2026 (refinement):
  //   "Due dates are 05/01/2026 & 06/01/2026, dlq after 05/10/2026 & 06/10/2026."
  const dueFragment = (() => {
    if (dueDates.length === 1) return `Due date is ${dueDates[0]}`;
    if (dueDates.length > 1) return `Due dates are ${dueDates.join(' & ')}`;
    return '';
  })();
  const dlqFragment = (() => {
    if (dlqDates.length === 1) return `dlq after ${dlqDates[0]}`;
    if (dlqDates.length > 1) return `dlq after ${dlqDates.join(' & ')}`;
    return '';
  })();
  const dateSentenceParts = [dueFragment, dlqFragment].filter(Boolean);
  const datesSentence = dateSentenceParts.length > 0 ? `${dateSentenceParts.join(', ')}.` : '';

  // Continuous-narrative format (refinement, Feb 19, 2026): join the
  // bill-status sentence and the dates sentence with a SPACE so the
  // paragraph reads as one continuous operational sentence, e.g.
  //   "2026 SB bill is paid. Due date is 03/03/2025, dlq after 03/04/2025."
  // rather than breaking onto a new line after "paid."
  let paragraph = datesSentence ? `${billSentence} ${datesSentence}` : billSentence;

  // Outstanding-balance call-out — woven into the same paragraph as a
  // continuation sentence rather than pushed onto a separate line.
  // Wording adapts to the worst status across the unpaid rows so the
  // sentence stays operationally accurate. Any status whose label
  // contains "delinquent" (Delinquent, Partially Delinquent, …) drives
  // the delinquent qualifier; everything else (Due, Partially Paid, …)
  // gets the neutral "unpaid" qualifier.
  const unpaid = rows.filter((r) => !isPaid(r));
  if (unpaid.length > 0) {
    const totalBalance = unpaid.reduce((sum, r) => sum + computeBalance(r), 0);
    if (totalBalance > 0.005) {
      const anyDelinquent = unpaid.some(
        (r) => (r.pay_status || '').toString().toLowerCase().includes('delinquent')
      );
      const qualifier = anyDelinquent ? 'unpaid delinquent' : 'unpaid';
      paragraph = `${paragraph} The remaining ${qualifier} balance is ${fmtUsd(totalBalance)}.`;
    }
  }

  return [`${heading}:`, paragraph].join('\n');
};

/**
 * Build the additive Specialty Tax Notes paragraph.
 *
 * Feb 2026 (P0 — Mixed Category Notes):
 *   Utility specialty rows (Solid Waste / Water / Sewer / Storm Water /
 *   Water & Sewer) are NO LONGER emitted here. The standard narrative
 *   engine (`generateTaxCertificationNotes`) now renders them inline
 *   as their own labelled sub-block (e.g. `SOLID WASTE:\n…`) using the
 *   same annual/semi-annual/quarterly narrative as standard taxes, so a
 *   county with both real-estate AND Solid Waste installments gets two
 *   cleanly-separated paragraphs instead of one confusing merged
 *   narrative trailed by an operational "Utility Bills: …bill is paid…"
 *   stub.
 *
 * Supplemental Bills (SB / SB-1 / SB-2) and Special Assessments
 * (SA / SA-1) keep their operational additive block format because
 * their wording semantics differ from a standard annual narrative
 * (ordinal-specific bill IDs, mixed-status grouping, balance sentence).
 *
 * Example output (SB/SA only):
 *   Supplemental Bills:
 *   2026 1st SB bill is paid and 2nd SB bill is due.
 *   Due dates are 05/01/2026 & 06/01/2026, dlq after 05/10/2026 & 06/10/2026.
 */
export const generateSpecialtyTaxNotes = (installments = []) => {
  if (!Array.isArray(installments) || installments.length === 0) return '';

  const sbRows = [];
  const saRows = [];

  for (const inst of installments) {
    const cat = getSpecialtyCategory(inst.installment_name);
    if (cat === 'supplemental_bill') sbRows.push(inst);
    else if (cat === 'special_assessment') saRows.push(inst);
    // Utility specialty (Solid Waste / Water / …) intentionally skipped
    // — rendered inline by the standard narrative engine. See JSDoc.
  }

  const blocks = [];
  if (sbRows.length > 0) blocks.push(groupSentence('Supplemental Bills', sbRows));
  if (saRows.length > 0) blocks.push(groupSentence('Special Assessments', saRows));

  return blocks.join('\n\n');
};

// ── TAXCERT-NOTES-BILL-WORDING-001 (Sep 2026) — Utility Bill wording ──
// Utility sub-blocks are narrated by the standard annual / semi-annual /
// quarterly templates. Client-facing wording for Utility installments must
// say "Bill/Bills" (never "tax/taxes") and use the utility name. This is a
// PRESENTATION-ONLY transform over the finished paragraph: years, statuses,
// dates, amounts and sentence order are produced by the engine untouched.
// Only the explicit, tested phrase patterns below are rewritten — there is
// deliberately NO blind "tax → Bill" fallback.
const YEAR_LIST = '((?:19|20)\\d{2}(?:, (?:19|20)\\d{2})*(?: and (?:19|20)\\d{2})?)';

export const toUtilityBillWording = (text, utilityName) => {
  if (!text) return text || '';
  const name = String(utilityName || '').trim();
  const bills = name ? `${name} Bills` : 'Bills';
  let t = String(text);
  // Preambles
  t = t.replace(/\bAll prior taxes are paid\./g, `All prior ${bills} are paid.`);
  t = t.replace(/\bAll taxes prior to ([^.]*?) are paid\./g, `All ${bills} prior to $1 are paid.`);
  // Collection frequency
  t = t.replace(/\bTaxes are collected (annually|semi-annually|quarterly|monthly)\./g, 'Bills are collected $1.');
  t = t.replace(/\bTaxes are paid (semi-annually|quarterly)/g, 'Bills are paid $1');
  // Annual year/status sentences ("2025 taxes are paid.", "2023, 2024 and 2025 taxes are delinquent.", fee / discount / penalty variants)
  t = t.replace(new RegExp(`\\b${YEAR_LIST} taxes are `, 'g'), `$1 ${bills} are `);
  // Semi-annual / quarterly sentence fragments
  t = t.replace(/\bfirst and second installment taxes are /g, `first and second installment ${bills} are `);
  t = t.replace(/\bfirst installment taxes are /g, `first installment ${bills} are `);
  t = t.replace(/\bsecond installment taxes are /g, `second installment ${bills} are `);
  t = t.replace(/\bSecond installment taxes are /g, `Second installment ${bills} are `);
  t = t.replace(/\bquarter taxes are /g, `quarter ${bills} are `);
  // Mailing / projection sentences
  t = t.replace(new RegExp(`\\b${YEAR_LIST} tax bills are expected to be mailed`, 'g'), `$1 ${bills} are expected to be mailed`);
  t = t.replace(new RegExp(`\\b${YEAR_LIST} taxes will be mailed`, 'g'), `$1 ${bills} will be mailed`);
  // Due / delinquency sentences
  t = t.replace(/\bTaxes due date is /g, 'The due date is ');
  t = t.replace(/, and taxes become delinquent after /g, ', and Bills become delinquent after ');
  t = t.replace(/\bTaxes become delinquent after the applicable delinquency dates\./g, 'Bills become delinquent after the applicable delinquency dates.');
  // Fully-exempt sentence
  t = t.replace(/\bFor taxes, the homeowner qualifies/g, `For ${bills}, the homeowner qualifies`);
  t = t.replace(/\bthe tax amounts are zero\./g, 'the Bill amounts are zero.');
  return t;
};

// Regression guard used by tests: true when a Utility/Special-Assessment
// paragraph still carries tax/taxes wording.
export const hasTaxWording = (text) => /\btax(?:es)?\b/i.test(String(text || ''));

// ── Stale-specialty stripper (idempotent re-append guarantee) ──────
// Headings that mark a Specialty Tax Notes block. Any paragraph whose
// first non-empty line equals one of these is treated as stale and
// removed before the freshly-generated block is appended.
const SPECIALTY_BLOCK_HEADINGS = [
  'Supplemental Bills:',
  'Special Assessments:',
  'Utility Bills:',
];

const stripSpecialtyBlocks = (notes) => {
  if (!notes) return '';
  const text = String(notes).trim();
  if (!text) return '';
  // generateSpecialtyTaxNotes separates blocks with `\n\n`; the standard
  // engine uses the same separator between top-level paragraphs.
  return text
    .split('\n\n')
    .filter((block) => {
      const firstLine = block.replace(/^\s+/, '').split('\n', 1)[0].trim();
      return !SPECIALTY_BLOCK_HEADINGS.includes(firstLine);
    })
    .join('\n\n')
    .replace(/\s+$/, '');
};

/**
 * Append the Specialty Tax Notes block to an existing notes string
 * produced by the standard notes engine. Safe to call with empty/null
 * `engineNotes` — returns just the specialty block, or empty string if
 * neither is present.
 *
 * Idempotent against stale specialty paragraphs — any pre-existing
 * Supplemental Bills / Special Assessments / Utility Bills block in
 * `engineNotes` is stripped before the fresh block is appended.
 */
export const appendSpecialtyNotes = (engineNotes, installments) => {
  const specialty = generateSpecialtyTaxNotes(installments);
  const cleaned = stripSpecialtyBlocks(engineNotes);
  if (!specialty) return cleaned;
  if (!cleaned) return specialty;
  return `${cleaned}\n\n${specialty}`;
};

/**
 * Partition a list of installments into [standardRows, specialtyRows]
 * so the Tax Collection regenerator can rebuild the standard half from
 * frequency labels while preserving specialty rows verbatim.
 */
export const partitionInstallments = (installments = []) => {
  const standard = [];
  const specialty = [];
  if (!Array.isArray(installments)) return [standard, specialty];
  for (const inst of installments) {
    if (isSpecialtyInstallment(inst?.installment_name)) {
      specialty.push(inst);
    } else {
      standard.push(inst);
    }
  }
  return [standard, specialty];
};

// ── PDF-parity helpers (Feb 19, 2026 refinement) ────────────────────
// These mirror the suffix / parenthetical logic that the backend
// pdf_service.py applies to the Current Tax Summary table so React
// preview screens render the SAME visual annotations as the PDF.

const INSTALLMENT_YEAR_SUFFIX = {
  '1st half': '-1', '1st': '-1', 'first half': '-1', 'first': '-1', '1': '-1',
  '2nd half': '-2', '2nd': '-2', 'second half': '-2', 'second': '-2', '2': '-2',
  'q1': '-1', '1st quarter': '-1',
  'q2': '-2', '2nd quarter': '-2',
  'q3': '-3', '3rd quarter': '-3', '3rd': '-3', '3': '-3',
  'q4': '-4', '4th quarter': '-4', '4th': '-4', '4': '-4',
  // Specialty Supplemental Bill year suffix.
  'sb-1': '-1',
  'sb-2': '-2',
};

/**
 * Compute the user-facing Tax Year label (`2026`, `2026-1`, `2026-2`, …)
 * for a single installment row. Returns an empty string when `tax_year`
 * is blank — never throws on bad input.
 *
 * Mirrors `pdf_service.py::_INST_SUFFIX` so the React Current Tax
 * Summary table and the PDF render identical strings.
 */
export const formatTaxYearDisplay = (taxYear, installmentName) => {
  const yr = (taxYear || '').toString().trim();
  if (!yr) return '';
  const key = (installmentName || '').toString().trim().toLowerCase();
  const suffix = INSTALLMENT_YEAR_SUFFIX[key] || '';
  return `${yr}${suffix}`;
};

/**
 * Specialty parenthetical that should appear UNDER the jurisdiction
 * name in the Current Tax Summary table (and the PDF).
 *   SB-1 → "(SB-1)"
 *   Water & Sewer → "(Water & Sewer)"
 *   1st Half → "" (standard rows get no annotation)
 *
 * Case-insensitive; returns empty string for non-specialty labels.
 */
export const specialtyParenthetical = (installmentName) => {
  const raw = (installmentName || '').toString().trim();
  if (!raw) return '';
  return isSpecialtyInstallment(raw) ? `(${raw})` : '';
};

/**
 * Convenience: returns the FIRST specialty label found across a
 * jurisdiction's installments — used to show a small `(SB-1)`
 * annotation under the jurisdiction name in the sidebar list. If the
 * jurisdiction mixes multiple specialty types, returns a `(SB-1, Water)`
 * comma-joined fragment so the UI never silently drops one.
 */
export const jurisdictionSpecialtyLabel = (installments = []) => {
  if (!Array.isArray(installments) || installments.length === 0) return '';
  const seen = [];
  for (const inst of installments) {
    const name = (inst?.installment_name || '').toString().trim();
    if (name && isSpecialtyInstallment(name) && !seen.includes(name)) {
      seen.push(name);
    }
  }
  if (seen.length === 0) return '';
  return `(${seen.join(', ')})`;
};
